import { safeParseJson } from '@/utils/safeParseJson';

/**
 * Build target devices for Lureo stories.
 * Stored as scene_instance_payload.build_device to tailor the editor and runtime per device.
 * Extensible: add new device keys and labels here as we support more devices.
 *
 * `build_device` used to share the `devices` key with the publish flow's platform array, so
 * publishing a scene clobbered its build target and the share-link resolver could not tell a
 * build target from a published platform (share links 3.8). Reads still honour the legacy
 * string on `devices` for scenes created before the split; new writes never touch `devices`.
 */
export const BUILD_DEVICE_KEYS = ['mobile', 'spectacles'] as const;
export type BuildDeviceKey = (typeof BUILD_DEVICE_KEYS)[number];

/** Human-readable labels for the device picker and editor pill */
export const BUILD_DEVICE_LABELS: Record<BuildDeviceKey, string> = {
  mobile: 'Mobile',
  spectacles: 'Spectacles'
};

export function getBuildDeviceLabel(deviceKey: string): string {
  return BUILD_DEVICE_LABELS[deviceKey as BuildDeviceKey] ?? deviceKey;
}

/** Build the scene_instance_payload fields for a given build target */
export function scenePayloadForDevice(device: BuildDeviceKey): { build_device: BuildDeviceKey } {
  return { build_device: device };
}

function asBuildDevice(value: unknown): BuildDeviceKey | null {
  return typeof value === 'string' && BUILD_DEVICE_KEYS.includes(value as BuildDeviceKey)
    ? (value as BuildDeviceKey)
    : null;
}

/** Effective build device from scene_instance_payload; defaults to mobile for older/empty payloads */
export function getEffectiveBuildDevice(
  payload: Record<string, unknown> | string | null | undefined
): BuildDeviceKey {
  const parsed = safeParseJson<Record<string, unknown>>(payload);
  return asBuildDevice(parsed?.build_device) ?? asBuildDevice(parsed?.devices) ?? 'mobile';
}
